import { useCallback, useMemo } from 'react'
import { useMessages } from './intl.hooks'

/**
 * Extracts the language code from a locale code
 * Examples:
 * - 'en_US' -> 'EN'
 * - 'fr_CA' -> 'FR'
 * - 'EN' -> 'EN' (already normalized)
 */
const extractLanguageCode = (localeCode: string): string => {
  // Handle full locale codes like 'en_US', 'fr_CA'
  if (localeCode.includes('_')) {
    return localeCode.split('_')[0].toUpperCase()
  }
  // Handle already normalized codes like 'EN', 'FR'
  return localeCode.toUpperCase()
}

export const useLanguageLabels = () => {
  const messages = useMessages({
    EN: 'components.localeSelect.locales.en',
    FR: 'components.localeSelect.locales.fr',
    ES: 'components.localeSelect.locales.es',
  })

  // Memoize the getByLocaleCode function to prevent new function references on every render
  const getByLocaleCode = useCallback(
    (localeCode: string): string | undefined => {
      const languageCode = extractLanguageCode(localeCode)
      return messages[languageCode as keyof typeof messages]
    },
    [messages]
  )

  // Memoize the entire returned object to prevent new object references on every render
  return useMemo(
    () => ({
      ...messages,
      getByLocaleCode,
    }),
    [messages, getByLocaleCode]
  )
}
